import { ReactComponent as ContextIcon } from 'module/ui/assets/svgs/context-icon.svg';
import { JSONContent } from '@tiptap/core';
import { CustomSectionType } from 'api/custom-section/types';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { CollaborativeEditor } from '../document-editor';
import { defaultExtensionConfig } from '../document-editor/types';

const QUESTION_ANSWER_ITEM = CustomSectionType.QuestionAnswerItem;

/** Section types allowed in the "all sections" version history list (sidebar open). */
const VERSION_HISTORY_LIST_SECTION_TYPES: CustomSectionType[] = [
  CustomSectionType.Question,
  CustomSectionType.Action,
  CustomSectionType.ExtractInfo,
  CustomSectionType.QuestionAnswerItem,
];

const LINES_PREVIEW = 4;
const LINES_FADED = 2;
const LINES_VISIBLE = LINES_PREVIEW + LINES_FADED;
const LINES_EXPAND_THRESHOLD = 3;

const TIMELINE_CELL_WIDTH = 24;

/** Collect plain text from a Tiptap JSON node (paragraphs, text nodes). */
function getTextFromNode(node: JSONContent): string {
  if (!node) {
    return '';
  }
  if (node.type === 'text' && node.text) {
    return node.text;
  }
  const content = node.content;

  if (!Array.isArray(content)) {
    return '';
  }

  return content.map(getTextFromNode).join('');
}

/** Find the first table node in doc content or return the node if it is a table. */
function findTableNode(
  content: JSONContent | JSONContent[],
): JSONContent | null {
  if (Array.isArray(content)) {
    const table = content.find((n) => n?.type === 'table');

    return table ?? null;
  }
  if (content?.type === 'table') {
    return content;
  }
  if (content?.type === 'doc' && Array.isArray(content.content)) {
    return findTableNode(content.content);
  }
  if (Array.isArray(content?.content)) {
    return findTableNode(content.content);
  }

  return null;
}

/**
 * For QUESTION_ANSWER_ITEM: first row = question (col0 + col1), second row = answer (col1).
 * Returns question heading string and the answer cell content (block nodes) for the editor.
 */
function getQuestionAnswerFromTable(
  tableNode: JSONContent,
): { questionHeading: string; answerContent: JSONContent[] } | null {
  const rows = tableNode.content;

  if (!Array.isArray(rows) || rows.length < 2) {
    return null;
  }
  const firstRow = rows[0];
  const secondRow = rows[1];

  if (firstRow?.type !== 'tableRow' || secondRow?.type !== 'tableRow') {
    return null;
  }
  const firstRowCells = firstRow.content ?? [];
  const secondRowCells = secondRow.content ?? [];
  const col0Text = getTextFromNode(firstRowCells[0] ?? {});
  const col1Text = getTextFromNode(firstRowCells[1] ?? {});
  const questionHeading = [col0Text, col1Text].filter(Boolean).join('. ');
  const answerCell = secondRowCells[1];
  const answerContent = Array.isArray(answerCell?.content)
    ? answerCell.content
    : [];

  return { questionHeading, answerContent };
}

const VersionHistoryScrollWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const VersionHistoryContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: auto;
  max-height: 100%;
  overflow-y: auto;
  margin: var(--spacing-05);
  border-radius: var(--border-radius-small);
`;

const VersionCard = styled.div`
  display: flex;
  flex-direction: column;
  background-color: var(--scarlet-gray-50);
  border-radius: var(--border-radius-small);
  position: relative;
  gap: var(--spacing-03);
`;

const VersionContentRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: var(--spacing-03);
  width: 100%;
`;

const VersionTimelineCell = styled.div`
  flex-shrink: 0;
  width: ${TIMELINE_CELL_WIDTH}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  height: 100%;
`;

const VersionTimelineLine = styled.div`
  border-left: 1px solid var(--button-ghost-border);
  width: 1px;
  height: 100%;
  margin-top: 8px;
`;

const VersionIconWrapper = styled.div<{ $isLatest?: boolean }>`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 6px;
  z-index: 1;

  svg circle {
    fill: ${({ $isLatest }) => ($isLatest ? '#FA6800' : 'var(--gray-60)')};
  }

  svg path {
    fill: ${({ $isLatest }) => ($isLatest ? '#FA6800' : 'var(--gray-60)')};
  }
`;

const VersionContentArea = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-02);
  margin-bottom: var(--spacing-06);
  margin-top: var(--spacing-03);

  .custom-section {
    padding-left: 0 !important;
  }
`;

const VersionMetaRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-01);
`;

const VersionUpdateStatus = styled.div<{ $isLatest?: boolean }>`
  font-size: var(--font-size-01);
  font-weight: var(--font-weight-semibold);
  color: var(--text-placeholder);
`;

const VersionAuthor = styled.div`
  font-size: var(--font-size-sm);
  color: var(--text-tertiary);
`;

const VersionPreviewWrapper = styled.div`
  position: relative;
  cursor: pointer;
`;

const VersionPreviewEditorWrapper = styled.div`
  position: relative;
  z-index: 0;
  max-height: calc(var(--line-height-02) * ${LINES_VISIBLE});
  min-height: 0;
  overflow: hidden;
  pointer-events: none;
`;

const VersionShadeOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: calc(var(--line-height-04) * ${LINES_FADED});
  background: linear-gradient(to bottom, transparent, var(--scarlet-gray-50));
  pointer-events: all;
  z-index: 1;
`;

const VersionEditorWrapper = styled.div`
  min-height: fit-content;
  max-height: 400px;
  overflow-y: auto;
`;

const VersionHistoryDisabledMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: var(--spacing-06);
  text-align: center;
  color: var(--text-tertiary);
  font-size: var(--font-size-01);
  line-height: var(--line-height-02);
`;

const AnswerHistoryHeadings = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-01);
  margin-bottom: var(--spacing-04);
`;

const AnswerHistorySecondHeading = styled.div`
  color: var(--text-primary);
  font-weight: var(--font-weight-bold);
`;

const SectionCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
  margin-bottom: var(--spacing-05);
  cursor: pointer;
  padding: var(--spacing-05);
  border-radius: var(--border-radius-small);
  transition: background-color 0.15s ease;
  background-color: var(--scarlet-gray-50);
  border-radius: var(--border-radius-small);
`;

export type DocumentVersion = {
  id: number;
  nodeId: number;
  nodeType: string;
  versionNumber: number;
  versionName: string;
  content: JSONContent;
  createdAt: string;
  updatedAt: string;
  createdBy?: number;
  createdByName?: string;
  createdByTitle?: string;
};

export type VersionHistoryGroup = {
  nodeId: number;
  nodeType: string;
  versions: DocumentVersion[];
  sectionTitle?: string;
  /** Section type (e.g. QUESTION_ANSWER_ITEM) for special rendering (answer-only cards). */
  sectionType?: string;
  sectionId?: number;
};

const READONLY_EDITOR_CONFIG = {
  ...defaultExtensionConfig,
  customSectionConfig: {
    allowResolve: false as const,
    generateTemplateSectionId: false,
  },
  collaborationConfig: { enabled: false as const },
  autoSaveConfig: { enabled: false as const },
  askAiConfig: { enabled: false as const },
  commandsPickerConfig: { enabled: false as const },
  commandPickerWithTriggerConfig: { enabled: false as const },
  elementInspectorConfig: { enabled: false },
  likeDislikeConfig: { enabled: false },
  tableToolbarConfig: { enabled: false },
  distractionFreeConfig: { onDistractionFreeStateChange: () => {} },
  endOfDocumentConfig: { enabled: false },
  tableOfContentsConfig: { enabled: false },
  sectionMenuConfig: { enabled: false },
};

interface VersionVersionCardProps {
  version: DocumentVersion;
  isLatest: boolean;
  isLast: boolean;
  /** When set (e.g. for QUESTION_ANSWER_ITEM), editor shows this instead of version.content (doc content array). */
  displayContent?: JSONContent[];
  /** Called before expanding so the parent can preserve scroll position. */
  onBeforeExpand?: () => void;
}

function VersionVersionCard({
  version,
  isLatest,
  isLast,
  displayContent,
  onBeforeExpand,
}: VersionVersionCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const previewWrapperRef = useRef<HTMLDivElement>(null);

  const editorValue: JSONContent =
    displayContent != null
      ? { type: 'doc', content: displayContent }
      : version.content;

  const handlePreviewClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onBeforeExpand?.();
      setIsExpanded(true);
    },
    [onBeforeExpand],
  );

  useEffect(() => {
    if (isExpanded || !previewWrapperRef.current) {
      return;
    }
    const el = previewWrapperRef.current;
    const measure = () => {
      const lineHeightPx = parseFloat(getComputedStyle(el).lineHeight) || 24;
      const threshold = LINES_EXPAND_THRESHOLD * lineHeightPx;
      const contentHeight = el.scrollHeight;

      if (contentHeight > 0 && contentHeight <= threshold) {
        setIsExpanded(true);
      }
    };

    const t = setTimeout(measure, 200);

    return () => clearTimeout(t);
  }, [isExpanded, version.id]);

  const formattedDate = version.updatedAt
    ? moment(version.updatedAt).format('D MMM')
    : '';

  const authorLabel =
    version.createdByName && version.createdByTitle
      ? `${version.createdByName} (${version.createdByTitle})`
      : (version.createdByName ?? null);

  return (
    <VersionCard>
      <VersionContentRow>
        <VersionTimelineCell>
          <VersionIconWrapper $isLatest={isLatest}>
            <ContextIcon width={16} height={16} />
          </VersionIconWrapper>
          {!isLast && <VersionTimelineLine />}
        </VersionTimelineCell>
        <VersionContentArea>
          <VersionMetaRow>
            <VersionUpdateStatus $isLatest={isLatest}>
              {isLatest ? (
                <FormattedMessage id="document.version_history.last_updated" />
              ) : formattedDate ? (
                <FormattedMessage
                  id="document.version_history.updated_on"
                  values={{ date: formattedDate }}
                />
              ) : (
                <FormattedMessage id="document.version_history.updated" />
              )}
            </VersionUpdateStatus>
            {authorLabel && <VersionAuthor>{authorLabel}</VersionAuthor>}
          </VersionMetaRow>
          {!isExpanded ? (
            <VersionPreviewWrapper
              onClick={handlePreviewClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  onBeforeExpand?.();
                  setIsExpanded(true);
                }
              }}
            >
              <VersionPreviewEditorWrapper ref={previewWrapperRef}>
                <CollaborativeEditor
                  value={editorValue}
                  extensionConfig={READONLY_EDITOR_CONFIG}
                  readonly={true}
                  placeholder=""
                />
              </VersionPreviewEditorWrapper>
              <VersionShadeOverlay />
            </VersionPreviewWrapper>
          ) : (
            <VersionEditorWrapper tabIndex={-1}>
              <CollaborativeEditor
                value={editorValue}
                extensionConfig={READONLY_EDITOR_CONFIG}
                readonly={true}
                placeholder=""
              />
            </VersionEditorWrapper>
          )}
        </VersionContentArea>
      </VersionContentRow>
    </VersionCard>
  );
}

export interface VersionHistoryContentProps {
  versionHistory: VersionHistoryGroup[];
  activeNodeId?: number;
  activeNodeType?: string;
  onScrollToSection?: (nodeId: number) => void;
}

/** Groups with more than one version (for "all sections" view when opened from sidebar). */
const groupsWithMultipleVersions = (groups: VersionHistoryGroup[]) =>
  groups.filter(
    (g) =>
      g.versions.length > 1 &&
      (VERSION_HISTORY_LIST_SECTION_TYPES.includes(
        g.sectionType as CustomSectionType,
      ) ||
        VERSION_HISTORY_LIST_SECTION_TYPES.includes(
          g.nodeType as CustomSectionType,
        )),
  );

function useGroupVersionDisplay(group: VersionHistoryGroup | null) {
  const sortedVersions = useMemo(
    () =>
      group
        ? [...group.versions].sort((a, b) => b.versionNumber - a.versionNumber)
        : [],
    [group],
  );

  const isQuestionAnswerItem =
    group != null &&
    (group.sectionType === QUESTION_ANSWER_ITEM ||
      group.nodeType === QUESTION_ANSWER_ITEM);

  const questionAnswerMeta = useMemo(() => {
    if (!isQuestionAnswerItem || sortedVersions.length === 0) {
      return null;
    }
    const firstContent = sortedVersions[0].content;
    const table = findTableNode(
      firstContent?.type === 'doc'
        ? (firstContent.content ?? [])
        : firstContent,
    );

    if (!table) {
      return null;
    }

    return getQuestionAnswerFromTable(table);
  }, [isQuestionAnswerItem, sortedVersions]);

  const getDisplayContentForVersion = useCallback(
    (version: DocumentVersion): JSONContent[] | undefined => {
      if (!isQuestionAnswerItem || !questionAnswerMeta) {
        return undefined;
      }
      const raw = version.content;
      const table = findTableNode(
        raw?.type === 'doc' ? (raw.content ?? []) : raw,
      );

      if (!table) {
        return undefined;
      }
      const parsed = getQuestionAnswerFromTable(table);

      return parsed?.answerContent ?? undefined;
    },
    [isQuestionAnswerItem, questionAnswerMeta],
  );

  return {
    sortedVersions,
    isQuestionAnswerItem,
    questionAnswerMeta,
    getDisplayContentForVersion,
  };
}

/** Renders version cards for a single group (used in both single-section and all-sections view). */
function GroupVersionCards({
  group,
  onBeforeExpand,
}: {
  group: VersionHistoryGroup;
  onBeforeExpand?: () => void;
}) {
  const {
    sortedVersions,
    isQuestionAnswerItem,
    questionAnswerMeta,
    getDisplayContentForVersion,
  } = useGroupVersionDisplay(group);

  return (
    <>
      {isQuestionAnswerItem && questionAnswerMeta && (
        <AnswerHistoryHeadings>
          {questionAnswerMeta.questionHeading && (
            <AnswerHistorySecondHeading>
              {questionAnswerMeta.questionHeading}
            </AnswerHistorySecondHeading>
          )}
        </AnswerHistoryHeadings>
      )}
      {sortedVersions.map((version, index) => (
        <VersionVersionCard
          key={version.id}
          version={version}
          isLatest={index === 0}
          isLast={index === sortedVersions.length - 1}
          displayContent={getDisplayContentForVersion(version)}
          onBeforeExpand={onBeforeExpand}
        />
      ))}
    </>
  );
}

/** Renders only the version history pane content (for use inside a unified side pane). */
export const VersionHistoryContent = ({
  versionHistory,
  activeNodeId,
  activeNodeType,
  onScrollToSection,
}: VersionHistoryContentProps) => {
  const openedFromSidebar = !activeNodeId || !activeNodeType;
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const onBeforeExpand = useCallback(() => {
    const el = scrollContainerRef.current;

    if (!el) {
      return;
    }

    const scrollTop = el.scrollTop;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollTop;
        }
      });
    });
  }, []);

  const filteredGroup = useMemo(() => {
    if (!activeNodeId || !activeNodeType) {
      return null;
    }

    return (
      versionHistory.find(
        (g) => g.nodeId === activeNodeId && g.nodeType === activeNodeType,
      ) ?? null
    );
  }, [versionHistory, activeNodeId, activeNodeType]);

  const sectionsWithMultipleVersions = useMemo(
    () => groupsWithMultipleVersions(versionHistory),
    [versionHistory],
  );

  if (versionHistory.length === 0) {
    return null;
  }

  // Opened from sidebar (manual icon click): show all section cards for sections with >1 version
  if (openedFromSidebar) {
    if (sectionsWithMultipleVersions.length === 0) {
      return (
        <VersionHistoryDisabledMessage>
          <FormattedMessage id="document.version_history.disabled_message" />
        </VersionHistoryDisabledMessage>
      );
    }

    return (
      <VersionHistoryScrollWrapper>
        <VersionHistoryContainer ref={scrollContainerRef}>
          {sectionsWithMultipleVersions.map((group) => (
            <SectionCardWrapper
              role="button"
              key={`${group.nodeType}-${group.nodeId}`}
              tabIndex={0}
              onClick={() => onScrollToSection?.(group.sectionId ?? 0)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onScrollToSection?.(group.sectionId ?? 0);
                }
              }}
            >
              <GroupVersionCards
                group={group}
                onBeforeExpand={onBeforeExpand}
              />
            </SectionCardWrapper>
          ))}
        </VersionHistoryContainer>
      </VersionHistoryScrollWrapper>
    );
  }

  // Opened from trigger (click on version history near a section): show single section
  if (!filteredGroup) {
    return (
      <VersionHistoryDisabledMessage>
        <FormattedMessage id="document.version_history.disabled_message" />
      </VersionHistoryDisabledMessage>
    );
  }

  return (
    <VersionHistoryScrollWrapper>
      <VersionHistoryContainer ref={scrollContainerRef}>
        <SectionCardWrapper
          role="button"
          tabIndex={0}
          onClick={() => onScrollToSection?.(filteredGroup.sectionId ?? 0)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onScrollToSection?.(filteredGroup.sectionId ?? 0);
            }
          }}
        >
          <GroupVersionCards
            group={filteredGroup}
            onBeforeExpand={onBeforeExpand}
          />
        </SectionCardWrapper>
      </VersionHistoryContainer>
    </VersionHistoryScrollWrapper>
  );
};
